import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Search, MapPin, Plane, Ship, ChevronDown, X, Globe } from 'lucide-react';
import { portsByCountry, getPortsByRegion, searchPorts } from '../data/ports';

/**
 * PortSelector - A searchable dropdown for selecting ports
 * Organized by country and region, supports search by port code, name, or country
 */
const PortSelector = ({ 
  value, 
  onChange, 
  label, 
  placeholder = "Search or select port...",
  required = false,
  id,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const portsByRegionData = useMemo(() => getPortsByRegion(), []);
  const regions = Object.keys(portsByRegionData);

  // Get the display value for the selected port
  const selectedPortDisplay = useMemo(() => {
    if (!value) return '';
    // Search through all ports to find the matching one
    for (const [country, ports] of Object.entries(portsByCountry)) {
      const port = ports.find(p => p.code === value);
      if (port) {
        return `${port.code} - ${port.name}, ${country}`;
      }
    }
    return value; // Return the value as-is if not found
  }, [value]);

  // Filter ports based on search
  const filteredPorts = useMemo(() => {
    if (searchQuery.length >= 2) {
      return searchPorts(searchQuery);
    }
    return [];
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (port, country) => {
    onChange(port.code);
    setSearchQuery('');
    setIsOpen(false);
    setSelectedRegion(null);
    setSelectedCountry(null);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchQuery('');
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'air': return <Plane className="h-3 w-3 text-blue-500" />;
      case 'sea': return <Ship className="h-3 w-3 text-cyan-500" />;
      case 'both': return <Globe className="h-3 w-3 text-purple-500" />;
      default: return <MapPin className="h-3 w-3 text-slate-400" />;
    }
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <Label htmlFor={id} className="mb-1.5 block">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      
      {/* Selected Value / Search Input */}
      <div 
        className={`relative flex items-center border rounded-md bg-white cursor-pointer transition-colors
          ${isOpen ? 'border-cyan-500 ring-1 ring-cyan-500' : 'border-slate-200 hover:border-slate-300'}
        `}
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        <Search className="absolute left-3 h-4 w-4 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={isOpen ? searchQuery : (value ? selectedPortDisplay : '')}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={value ? selectedPortDisplay : placeholder}
          className="w-full pl-9 pr-16 py-2 bg-transparent outline-none text-sm"
          data-testid={`${id}-input`}
        />
        <div className="absolute right-2 flex items-center gap-1">
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-slate-100 rounded"
            >
              <X className="h-4 w-4 text-slate-400" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-[400px] overflow-hidden">
          {/* Search Results */}
          {searchQuery.length >= 2 && filteredPorts.length > 0 && (
            <div className="p-2 border-b border-slate-100">
              <div className="text-xs text-slate-500 px-2 mb-1">
                Search Results ({filteredPorts.length})
              </div>
              <div className="max-h-[200px] overflow-y-auto">
                {filteredPorts.map((port, idx) => (
                  <button
                    key={`${port.code}-${idx}`}
                    type="button"
                    onClick={() => handleSelect(port, port.country)}
                    className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-cyan-50 flex items-center gap-2
                      ${value === port.code ? 'bg-cyan-100' : ''}
                    `}
                  >
                    {getTypeIcon(port.type)}
                    <span className="font-mono font-semibold text-cyan-700">{port.code}</span>
                    <span className="text-slate-600 flex-1 truncate">{port.name}</span>
                    <span className="text-xs text-slate-400">{port.country}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {searchQuery.length >= 2 && filteredPorts.length === 0 && (
            <div className="p-4 text-center text-sm text-slate-500">
              No ports found for "{searchQuery}"
            </div>
          )}

          {/* Browse by Region/Country */}
          {searchQuery.length < 2 && (
            <div className="flex h-[350px]">
              {/* Regions Column */}
              <div className="w-1/3 border-r border-slate-100 overflow-y-auto bg-slate-50">
                <div className="p-2 text-xs font-semibold text-slate-500 sticky top-0 bg-slate-50">
                  REGIONS
                </div>
                {regions.map((region) => (
                  <button
                    key={region}
                    type="button"
                    onClick={() => {
                      setSelectedRegion(region);
                      setSelectedCountry(null);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-white transition-colors
                      ${selectedRegion === region ? 'bg-white font-medium text-cyan-700 border-l-2 border-cyan-500' : 'text-slate-600'}
                    `}
                  >
                    {region}
                  </button>
                ))}
              </div>

              {/* Countries Column */}
              <div className="w-1/3 border-r border-slate-100 overflow-y-auto">
                <div className="p-2 text-xs font-semibold text-slate-500 sticky top-0 bg-white">
                  {selectedRegion ? 'COUNTRIES' : 'Select a region'}
                </div>
                {selectedRegion && portsByRegionData[selectedRegion]?.map(({ country }) => (
                  <button
                    key={country}
                    type="button"
                    onClick={() => setSelectedCountry(country)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 transition-colors
                      ${selectedCountry === country ? 'bg-cyan-50 font-medium text-cyan-700' : 'text-slate-600'}
                    `}
                  >
                    {country}
                    <span className="text-xs text-slate-400 ml-1">
                      ({portsByCountry[country]?.length || 0})
                    </span>
                  </button>
                ))}
              </div>

              {/* Ports Column */}
              <div className="w-1/3 overflow-y-auto">
                <div className="p-2 text-xs font-semibold text-slate-500 sticky top-0 bg-white">
                  {selectedCountry ? 'PORTS' : 'Select a country'}
                </div>
                {selectedCountry && portsByCountry[selectedCountry]?.map((port) => (
                  <button
                    key={port.code}
                    type="button"
                    onClick={() => handleSelect(port, selectedCountry)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 transition-colors
                      ${value === port.code ? 'bg-cyan-100' : ''}
                    `}
                  >
                    <div className="flex items-center gap-2">
                      {getTypeIcon(port.type)}
                      <span className="font-mono font-semibold text-cyan-700">{port.code}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 pl-5 truncate">
                      {port.name}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quick tip */}
          <div className="p-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">
            Type 2+ characters to search, or browse by region → country → port
          </div>
        </div>
      )}
    </div>
  );
};

export default PortSelector;
